import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  SocialIntegrationsEndpoints as Endpoints,
  type AddIntegration1CRequest,
  type AddIntegration1CResponse,
  type AddIntegrationInOneStepRequest,
  type AddIntegrationInOneStepResponse,
  type AddIntegrationRequest,
  type AddIntegrationResponse,
  type CreateOrUpdateYookassaIntegrationRequest,
  type CreateOrUpdateYookassaIntegrationResponse,
  type DeleteIntegration1CRequest,
  type DeleteIntegration1CResponse,
  type DeleteIntegrationRequest,
  type DeleteIntegrationResponse,
  type DeleteMessageRequest,
  type DeleteMessageResponse,
  type DeleteYookassaIntegrationRequest,
  type DeleteYookassaIntegrationResponse,
  type EditMessageRequest,
  type EditMessageResponse,
  type EditOneStepSocialIntegrationRequest,
  type EditOneStepSocialIntegrationResponse,
  type EditSocialIntegrationRequest,
  type EditSocialIntegrationResponse,
  type GetActiveChannelsRequest,
  type GetActiveChannelsResponse,
  type GetActiveChannelsWithSenderRequest,
  type GetActiveChannelsWithSenderResponse,
  type GetIntegration1CByIdRequest,
  type GetIntegration1CByIdResponse,
  type GetPaymentMethodsRequest,
  type GetPaymentMethodsResponse,
  type GetInstagramPostsRequest,
  type GetInstagramPostsResponse,
  type GetIntegrationAuthStateRequest,
  type GetIntegrationAuthStateResponse,
  type GetSocialIntegrationByIdRequest,
  type GetSocialIntegrationByIdResponse,
  type GetSocialIntegrationOperatorsRequest,
  type GetSocialIntegrationOperatorsResponse,
  type GetSocialIntegrationsForSystemRequest,
  type GetSocialIntegrationsForSystemResponse,
  type GetSocialIntegrationsRequest,
  type GetSocialIntegrationsResponse,
  type GetSocialIntegrationsStatusRequest,
  type GetSocialIntegrationsStatusResponse,
  type GetSmsServiceStatisticsRequest,
  type GetSmsServiceStatisticsResponse,
  type GetUserSocialIntegrationsRequest,
  type GetUserSocialIntegrationsResponse,
  type GetYookassaGetCodeUrlRequest,
  type GetYookassaGetCodeUrlResponse,
  type GetYookassaIntegrationRequest,
  type GetYookassaIntegrationResponse,
  type ListByTypeRequest,
  type ListByTypeResponse,
  type RefreshIntegrationAuthDataRequest,
  type RefreshIntegrationAuthDataResponse,
  type RequestWhatsAppPairingCodeRequest,
  type RequestWhatsAppPairingCodeResponse,
  type RestartIntegrationRequest,
  type RestartIntegrationResponse,
  type SendMessageToClientAgainRequest,
  type SendMessageToClientAgainResponse,
  type SendMessageToClientRequest,
  type SendMessageToClientResponse,
  type SetReactionRequest,
  type SetReactionResponse,
  type SetSocialIntegrationOperatorsRequest,
  type SetSocialIntegrationOperatorsResponse,
  type TestIntegration1CRequest,
  type TestIntegration1CResponse,
  type UpdateIntegration1COptionsRequest,
  type UpdateIntegration1COptionsResponse,
  type UpdateIntegration1CRequest,
  type UpdateIntegration1CResponse,
  type UpdateSocialIntegrationEmailHandlerRequest,
  type UpdateSocialIntegrationEmailHandlerResponse,
} from '@app/api/SocialIntegrations/types';
import { SOCIAL_INTEGRATIONS } from '@app/constants';

export const REFRESH_INTEGRATION_AUTH_DATA_TIMEOUT_MS = 30_000;
export const SEND_MESSAGE_TO_CLIENT_TIMEOUT_MS = 60_000;

export const socialIntegrationsApi = createApi({
  // Unique reducerPath to avoid colliding with the legacy
  // `src/services/rtkQuery/SocialIntegrations` slice, which also used
  // `socialIntegrationsApi`. The shared path made one slice's middleware
  // process the other's mutation actions and crash on `invalidatesTags`.
  reducerPath: 'socialIntegrationsApi2',
  baseQuery: createApiBaseQuery(SOCIAL_INTEGRATIONS),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.RestartIntegration]: mutation<
      RestartIntegrationResponse,
      RestartIntegrationRequest
    >({
      query: (params) => ({
        url: Endpoints.RestartIntegration,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.RequestWhatsAppPairingCode]: mutation<
      RequestWhatsAppPairingCodeResponse,
      RequestWhatsAppPairingCodeRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.RequestWhatsAppPairingCode,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.AddIntegration]: mutation<
      AddIntegrationResponse,
      AddIntegrationRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddIntegration,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.TestIntegration1C]: mutation<
      TestIntegration1CResponse,
      TestIntegration1CRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.TestIntegration1C,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.AddIntegration1C]: mutation<
      AddIntegration1CResponse,
      AddIntegration1CRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddIntegration1C,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteIntegration1C]: mutation<
      DeleteIntegration1CResponse,
      DeleteIntegration1CRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteIntegration1C,
        method: Methods.DELETE,
        params,
        data,
      }),
    }),
    [Endpoints.GetIntegration1CById]: query<
      GetIntegration1CByIdResponse,
      GetIntegration1CByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetIntegration1CById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateIntegration1C]: mutation<
      UpdateIntegration1CResponse,
      UpdateIntegration1CRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateIntegration1C,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateIntegration1COptions]: mutation<
      UpdateIntegration1COptionsResponse,
      UpdateIntegration1COptionsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateIntegration1COptions,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.AddIntegrationInOneStep]: mutation<
      AddIntegrationInOneStepResponse,
      AddIntegrationInOneStepRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddIntegrationInOneStep,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetIntegrationAuthState]: query<
      GetIntegrationAuthStateResponse,
      GetIntegrationAuthStateRequest
    >({
      query: (params) => ({
        url: Endpoints.GetIntegrationAuthState,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.RefreshIntegrationAuthData]: query<
      RefreshIntegrationAuthDataResponse,
      RefreshIntegrationAuthDataRequest
    >({
      query: (params) => ({
        url: Endpoints.RefreshIntegrationAuthData,
        method: Methods.GET,
        params,
        timeout: REFRESH_INTEGRATION_AUTH_DATA_TIMEOUT_MS,
      }),
    }),
    [Endpoints.EditSocialIntegration]: mutation<
      EditSocialIntegrationResponse,
      EditSocialIntegrationRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditSocialIntegration,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.EditOneStepSocialIntegration]: mutation<
      EditOneStepSocialIntegrationResponse,
      EditOneStepSocialIntegrationRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditOneStepSocialIntegration,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SendMessageToClient]: mutation<
      SendMessageToClientResponse,
      SendMessageToClientRequest
    >({
      query: ({ data, logs, ...params }) => ({
        url: Endpoints.SendMessageToClient,
        method: Methods.POST,
        params,
        data,
        logs,
        timeout: SEND_MESSAGE_TO_CLIENT_TIMEOUT_MS,
      }),
    }),
    [Endpoints.SendMessageToClientAgain]: mutation<
      SendMessageToClientAgainResponse,
      SendMessageToClientAgainRequest
    >({
      query: (params) => ({
        url: Endpoints.SendMessageToClientAgain,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.EditMessage]: mutation<EditMessageResponse, EditMessageRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditMessage,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetReaction]: mutation<SetReactionResponse, SetReactionRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetReaction,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteMessage]: mutation<
      DeleteMessageResponse,
      DeleteMessageRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteMessage,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteIntegration]: mutation<
      DeleteIntegrationResponse,
      DeleteIntegrationRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteIntegration,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetSocialIntegrations]: query<
      GetSocialIntegrationsResponse,
      GetSocialIntegrationsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSocialIntegrations,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSocialIntegrationsForSystem]: query<
      GetSocialIntegrationsForSystemResponse,
      GetSocialIntegrationsForSystemRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSocialIntegrationsForSystem,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetActiveChannels]: query<
      GetActiveChannelsResponse,
      GetActiveChannelsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetActiveChannels,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetActiveChannelsWithSender]: query<
      GetActiveChannelsWithSenderResponse,
      GetActiveChannelsWithSenderRequest
    >({
      query: (params) => ({
        url: Endpoints.GetActiveChannelsWithSender,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSocialIntegrationById]: query<
      GetSocialIntegrationByIdResponse,
      GetSocialIntegrationByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSocialIntegrationById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateSocialIntegrationEmailHandler]: mutation<
      UpdateSocialIntegrationEmailHandlerResponse,
      UpdateSocialIntegrationEmailHandlerRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateSocialIntegrationEmailHandler,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetUserSocialIntegrations]: query<
      GetUserSocialIntegrationsResponse,
      GetUserSocialIntegrationsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserSocialIntegrations,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ListByType]: query<ListByTypeResponse, ListByTypeRequest>({
      query: (params) => ({
        url: Endpoints.ListByType,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSocialIntegrationOperators]: query<
      GetSocialIntegrationOperatorsResponse,
      GetSocialIntegrationOperatorsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSocialIntegrationOperators,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetSocialIntegrationOperators]: mutation<
      SetSocialIntegrationOperatorsResponse,
      SetSocialIntegrationOperatorsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetSocialIntegrationOperators,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetSocialIntegrationsStatus]: query<
      GetSocialIntegrationsStatusResponse,
      GetSocialIntegrationsStatusRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSocialIntegrationsStatus,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CreateOrUpdateYookassaIntegration]: mutation<
      CreateOrUpdateYookassaIntegrationResponse,
      CreateOrUpdateYookassaIntegrationRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateOrUpdateYookassaIntegration,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteYookassaIntegration]: mutation<
      DeleteYookassaIntegrationResponse,
      DeleteYookassaIntegrationRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteYookassaIntegration,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetYookassaGetCodeUrl]: query<
      GetYookassaGetCodeUrlResponse,
      GetYookassaGetCodeUrlRequest
    >({
      query: (params) => ({
        url: Endpoints.GetYookassaGetCodeUrl,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetYookassaIntegration]: query<
      GetYookassaIntegrationResponse,
      GetYookassaIntegrationRequest
    >({
      query: (params) => ({
        url: Endpoints.GetYookassaIntegration,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetPaymentMethods]: query<
      GetPaymentMethodsResponse,
      GetPaymentMethodsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetPaymentMethods,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetInstagramPosts]: query<
      GetInstagramPostsResponse,
      GetInstagramPostsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetInstagramPosts,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSmsServiceStatistics]: query<
      GetSmsServiceStatisticsResponse,
      GetSmsServiceStatisticsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSmsServiceStatistics,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useRestartIntegrationMutation,
  useAddIntegrationMutation,
  useTestIntegration1CMutation,
  useAddIntegration1CMutation,
  useDeleteIntegration1CMutation,
  useGetIntegration1CByIdQuery,
  useLazyGetIntegration1CByIdQuery,
  useUpdateIntegration1CMutation,
  useUpdateIntegration1COptionsMutation,
  useAddIntegrationInOneStepMutation,
  useRefreshIntegrationAuthDataQuery,
  useLazyRefreshIntegrationAuthDataQuery,
  useGetIntegrationAuthStateQuery,
  useLazyGetIntegrationAuthStateQuery,
  useEditSocialIntegrationMutation,
  useEditOneStepSocialIntegrationMutation,
  useSendMessageToClientMutation,
  useSendMessageToClientAgainMutation,
  useEditMessageMutation,
  useSetReactionMutation,
  useDeleteMessageMutation,
  useDeleteIntegrationMutation,
  useGetSocialIntegrationsQuery,
  useLazyGetSocialIntegrationsQuery,
  useGetSocialIntegrationsForSystemQuery,
  useLazyGetSocialIntegrationsForSystemQuery,
  useGetActiveChannelsQuery,
  useLazyGetActiveChannelsQuery,
  useGetActiveChannelsWithSenderQuery,
  useLazyGetActiveChannelsWithSenderQuery,
  useGetSocialIntegrationByIdQuery,
  useLazyGetSocialIntegrationByIdQuery,
  useUpdateSocialIntegrationEmailHandlerMutation,
  useRequestWhatsAppPairingCodeMutation,
  useGetUserSocialIntegrationsQuery,
  useLazyGetUserSocialIntegrationsQuery,
  useListByTypeQuery,
  useLazyListByTypeQuery,
  useGetSocialIntegrationOperatorsQuery,
  useLazyGetSocialIntegrationOperatorsQuery,
  useSetSocialIntegrationOperatorsMutation,
  useGetSocialIntegrationsStatusQuery,
  useLazyGetSocialIntegrationsStatusQuery,
  useCreateOrUpdateYookassaIntegrationMutation,
  useDeleteYookassaIntegrationMutation,
  useGetYookassaGetCodeUrlQuery,
  useLazyGetYookassaGetCodeUrlQuery,
  useGetYookassaIntegrationQuery,
  useLazyGetYookassaIntegrationQuery,
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetInstagramPostsQuery,
  useLazyGetInstagramPostsQuery,
  useGetSmsServiceStatisticsQuery,
  useLazyGetSmsServiceStatisticsQuery,
} = socialIntegrationsApi;
